/**
 * Billing state for the current organization and the signed-in person: the
 * effective plan (org_plan()), the subscription behind it, the person's own
 * trial and subscriptions, and usage against the plan's limits.
 *
 * Gated by isBillingVisible(): while the plans are drafts, production runs no
 * billing query at all (the tables may not exist yet).
 */
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOrganization } from "@/hooks/useOrganization";
import { billingLive, isBillingVisible } from "@/lib/billing/gate";
import type { PlanSource, SubscriptionRowLite } from "@/lib/billing/orgPlan";
import { pickOrgSubscription, shouldStartTrial } from "@/lib/billing/view";
import { ENTITLING_STATUSES } from "../../supabase/functions/_shared/stripe-billing";
import type { PlanId } from "@/lib/pricing/plans";
import { track } from "@/lib/tracking/analytics";

// The billing tables aren't in the generated types until the migration is
// applied and `npm run gen-types` runs; until then they're read untyped with
// the row shapes declared here.
const db = supabase as unknown as SupabaseClient<any>;

export interface BillingState {
  /** org_plan() for the current organization. */
  plan: { planId: PlanId | null; status: string; source: PlanSource };
  /** The subscription linked to the current organization, if any. */
  orgSubscription: SubscriptionRowLite | null;
  /** The person's own subscriptions that still entitle (they pay for them). */
  ownSubscriptions: SubscriptionRowLite[];
  /** The person's app trial (whether one was ever started). */
  trial: { started_at: string; ends_at: string } | null;
  /** The current company's trial window: what the days left count down. */
  orgTrial: { ends_at: string } | null;
  /** When the company's free access ends (null: no end, or no free access). */
  freeUntil: string | null;
  /** Whether the person owns the current organization (only owners pay). */
  isOwner: boolean;
  /** Whether a Stripe customer exists, so the Portal has something to show. */
  hasCustomer: boolean;
  usage: { companies: number; fioAccounts: number; users: number };
}

export const billingQueryKey = (userId: string | undefined, orgId: string | undefined) =>
  ["billing", userId ?? "", orgId ?? ""] as const;

const SUBSCRIPTION_COLUMNS =
  "id, plan_id, billing_interval, status, livemode, past_due_since, current_period_end, cancel_at, cancel_at_period_end, trial_end";

/**
 * Stripe mode of this deployment's billing: test until the plans go live
 * (previews and the sandbox), live after. Rows of the other mode are ignored,
 * so a tester's sandbox purchase never shows up on their real account.
 */
function fetchBilling(userId: string, orgId: string, ownedOrgIds: string[]) {
  return fetchBillingInMode(userId, orgId, ownedOrgIds, billingLive());
}

async function fetchBillingInMode(
  userId: string,
  orgId: string,
  ownedOrgIds: string[],
  live: boolean
) {
  const [
    planRes,
    linkRes,
    ownRes,
    trialRes,
    orgTrialRes,
    overrideRes,
    customerRes,
    accountsRes,
    membersRes,
  ] = await Promise.all([
    db.rpc("org_plan", { org_id: orgId }),
    db
      .from("billing_subscription_organizations")
      .select("subscription_id")
      .eq("organization_id", orgId),
    db
      .from("billing_subscriptions")
      .select(SUBSCRIPTION_COLUMNS)
      .eq("owner_user_id", userId)
      .eq("livemode", live)
      .in("status", [...ENTITLING_STATUSES]),
    db.from("billing_trials").select("started_at, ends_at").eq("user_id", userId).maybeSingle(),
    db.from("billing_org_trials").select("ends_at").eq("organization_id", orgId).maybeSingle(),
    db.from("billing_org_overrides").select("until").eq("organization_id", orgId).maybeSingle(),
    // A customer deleted in Stripe has no Portal to open.
    db
      .from("billing_customers")
      .select("livemode")
      .eq("user_id", userId)
      .eq("livemode", live)
      .is("deleted_at", null),
    ownedOrgIds.length
      ? supabase
          .from("bank_accounts")
          .select("id", { count: "exact", head: true })
          .in("organization_id", ownedOrgIds)
      : Promise.resolve({ count: 0, error: null }),
    ownedOrgIds.length
      ? supabase.from("organization_members").select("user_id").in("organization_id", ownedOrgIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  for (const res of [
    planRes,
    linkRes,
    ownRes,
    trialRes,
    orgTrialRes,
    overrideRes,
    customerRes,
    accountsRes,
    membersRes,
  ]) {
    if (res.error) throw res.error;
  }

  const planRow = (
    planRes.data as Array<{ plan_id: PlanId | null; status: string; source: PlanSource }>
  )?.[0];
  const linkedIds = ((linkRes.data ?? []) as Array<{ subscription_id: string }>).map(
    (l) => l.subscription_id
  );

  let orgSubscription: SubscriptionRowLite | null = null;
  if (linkedIds.length) {
    const { data, error } = await db
      .from("billing_subscriptions")
      .select(SUBSCRIPTION_COLUMNS)
      .in("id", linkedIds);
    if (error) throw error;
    orgSubscription = pickOrgSubscription((data ?? []) as SubscriptionRowLite[], live);
  }

  const memberIds = new Set(
    ((membersRes.data ?? []) as Array<{ user_id: string }>).map((m) => m.user_id)
  );

  return {
    plan: planRow ?? { plan_id: null, status: "none", source: "none" as PlanSource },
    orgSubscription,
    ownSubscriptions: (ownRes.data ?? []) as SubscriptionRowLite[],
    trial: (trialRes.data as BillingState["trial"]) ?? null,
    orgTrial: (orgTrialRes.data as BillingState["orgTrial"]) ?? null,
    freeUntil: (overrideRes.data as { until: string | null } | null)?.until ?? null,
    hasCustomer: ((customerRes.data ?? []) as unknown[]).length > 0,
    usage: {
      companies: ownedOrgIds.length,
      fioAccounts: accountsRes.count ?? 0,
      users: memberIds.size,
    },
  };
}

export function useBilling() {
  const visible = isBillingVisible();
  const { data: user } = useCurrentUser();
  const { currentOrganization, organizations } = useOrganization();
  const queryClient = useQueryClient();
  const ownedOrgIds = organizations.filter((o) => o.role === "owner").map((o) => o.id);
  const isOwner = currentOrganization?.role === "owner";
  const orgId = currentOrganization?.id;

  const query = useQuery({
    queryKey: billingQueryKey(user?.id, orgId),
    enabled: visible && !!user?.id && !!orgId,
    staleTime: 60_000,
    queryFn: async (): Promise<BillingState> => {
      const r = await fetchBilling(user!.id, orgId!, ownedOrgIds);
      return {
        plan: { planId: r.plan.plan_id, status: r.plan.status, source: r.plan.source },
        orgSubscription: r.orgSubscription,
        ownSubscriptions: r.ownSubscriptions,
        trial: r.trial,
        orgTrial: r.orgTrial,
        freeUntil: r.freeUntil,
        isOwner,
        hasCustomer: r.hasCustomer,
        usage: r.usage,
      };
    },
  });

  return { visible, ...query };
}

/**
 * Starts a company owner's 14-day trial the first time they use the app,
 * once billing is live (the RPC is a no-op when it already ran). Not on
 * previews: a reviewer's trial would be spent before launch. Mounted once,
 * by AppLayout, on every app page: with enforcement on, a new account is
 * read-only until this runs, and the read-only banner replaces
 * BillingBanner, so the start must not depend on any banner.
 */
export function useTrialAutoStart() {
  const { data } = useBilling();
  const { organizations } = useOrganization();
  const queryClient = useQueryClient();
  const ownedOrgs = organizations.filter((o) => o.role === "owner").length;
  const trialRequested = useRef(false);

  useEffect(() => {
    if (!data || trialRequested.current) return;
    const start = shouldStartTrial({
      live: billingLive(),
      hasTrial: !!data.trial,
      ownSubscriptions: data.ownSubscriptions.length,
      ownedOrgs,
      planSource: data.plan.source,
    });
    if (!start) return;
    trialRequested.current = true;
    db.rpc("billing_start_trial").then(({ error }) => {
      if (error) {
        console.error("[useTrialAutoStart] billing_start_trial:", error.message);
        return;
      }
      // Billing state and entitlements (["billing", "entitlements", …]) both refetch.
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    });
  }, [data, ownedOrgs, queryClient]);
}

export type BillingErrorCode =
  | "already_subscribed"
  | "not_owner"
  | "no_customer"
  | "price_mismatch"
  | "billing_not_configured"
  | "rate_limited"
  | "test_mode_org_only"
  | "failed";

/** Read `{ error }` from a non-2xx edge function response. */
async function errorCode(error: unknown): Promise<BillingErrorCode> {
  const context = (error as { context?: Response })?.context;
  if (context && typeof context.json === "function") {
    try {
      const body = await context.clone().json();
      if (typeof body?.error === "string") return body.error as BillingErrorCode;
    } catch {
      // not JSON
    }
  }
  return "failed";
}

/** Opens Stripe Checkout; resolves with an error code when it can't. */
export async function startCheckout(input: {
  plan: PlanId;
  organizationId: string;
  lang: string;
}): Promise<BillingErrorCode | null> {
  const { data, error } = await supabase.functions.invoke("billing-create-checkout", {
    body: input,
  });
  if (error) return errorCode(error);
  if (!data?.url) return "failed";
  track("checkout_started", { plan: input.plan });
  window.location.assign(data.url);
  return null;
}

/** Opens the Stripe Customer Portal; resolves with an error code when it can't. */
export async function openPortal(
  lang: string,
  flow?: "change_plan"
): Promise<BillingErrorCode | null> {
  const { data, error } = await supabase.functions.invoke("billing-portal", {
    body: { lang, flow },
  });
  if (error) return errorCode(error);
  if (!data?.url) return "failed";
  window.location.assign(data.url);
  return null;
}
